import {Async, Empty, isLoaded, Loaded} from 'Core/models/Async';
import {createValidatableEmptyEmail, ValidatableEmail} from 'Core/models/Email';
import {EmptyString} from 'Core/models/EmptyString';
import {createValidatableEmptyString, ValidatableNonEmptyString} from 'Core/models/ValidatableNonEmptyString';
import {createValidatableEmptyPhone, ValidatablePhone} from 'Core/models/PhoneNumber';
import {none, Option, some} from 'fp-ts/lib/Option';
import {Lens} from 'monocle-ts';
import {ValidRegionCode} from 'Agent/Policies/models/RegionCode';
import {createAction} from 'Shared/utilities/actionCreator';
import {AvailableFieldSalesLeaderPayload} from 'Users/models/AvailableFieldSalesLeaderPayload';
import {
  FieldVicePresident,
  UnvalidatedFieldVicePresident,
  validateFieldVicePresident,
  ValidFieldVicePresident,
} from 'Users/models/FieldVicePresident';

export enum FieldVicePresidentUserActionType {
  LOAD_FVP_USER = 'fieldVicePresidentUser/LOAD_FVP_USER',
  FETCH_FVP_USER = 'fieldVicePresidentUser/FETCH_FVP_USER',
  CHANGE_USER = 'fieldVicePresidentUser/CHANGE_USER',
  GET_AVAILABLE_FSLS = 'fieldVicePresidentUser/GET_AVAILABLE_FSLS',
  CHANGE_AVAILABLE_FSLS = 'fieldVicePresidentUser/CHANGE_AVAILABLE_FSLS',
  SAVE_FVP_USER = 'fieldVicePresidentUser/SAVE_FSL_USER',
  GET_REGIONS = 'fieldVicePresidentUser/GET_REGIONS',
  CHANGE_REGIONS = 'fieldVicePresidentUser/CHANGE_REGIONS',
  CLEAR = 'fieldVicePresidentUser/CLEAR',
  VALIDATE_USERNAME = 'fieldVicePresidentUser/VALIDATE_USERNAME',
  SET_USERNAME = 'fieldVicePresidentUser/SET_USERNAME',
  VALIDATE_EMAIL = 'fieldVicePresidentUser/VALIDATE_EMAIL',
  SET_EMAIL = 'fieldVicePresidentUser/SET_EMAIL',
  VALIDATE_PHONE = 'fieldVicePresidentUser/VALIDATE_PHONE',
  SET_PHONE = 'fieldVicePresidentUser/SET_PHONE',
}

export const getAvailableFSLS = () => createAction(FieldVicePresidentUserActionType.GET_AVAILABLE_FSLS);
export const changeAvailableFSLs = (availableFsls: Async<AvailableFieldSalesLeaderPayload[]>) =>
  createAction(FieldVicePresidentUserActionType.CHANGE_AVAILABLE_FSLS, availableFsls);
export const validatePhone = (phone: string) => createAction(FieldVicePresidentUserActionType.VALIDATE_PHONE, phone);
export const setPhone = (phone: ValidatablePhone) => createAction(FieldVicePresidentUserActionType.SET_PHONE, phone);
export const validateEmail = (email: string) => createAction(FieldVicePresidentUserActionType.VALIDATE_EMAIL, email);
export const setEmail = (email: ValidatableEmail) => createAction(FieldVicePresidentUserActionType.SET_EMAIL, email);
export const validateUsername = (username: string) =>
  createAction(FieldVicePresidentUserActionType.VALIDATE_USERNAME, username);
export const setUsername = (username: ValidatableNonEmptyString) =>
  createAction(FieldVicePresidentUserActionType.SET_USERNAME, username);
export const clearFieldVicePresident = () => createAction(FieldVicePresidentUserActionType.CLEAR);
export const loadFvpUser = (user: Async<FieldVicePresident>) =>
  createAction(FieldVicePresidentUserActionType.LOAD_FVP_USER, user);
export const getFvpUser = (userId: string) => createAction(FieldVicePresidentUserActionType.FETCH_FVP_USER, {userId});
export const getRegions = () => createAction(FieldVicePresidentUserActionType.GET_REGIONS);
export const changeRegions = (regions: Async<ValidRegionCode[]>) =>
  createAction(FieldVicePresidentUserActionType.CHANGE_REGIONS, regions);
export const changeFieldVicePresidentUser = (fieldSalesLeader: Async<FieldVicePresident>) =>
  createAction(FieldVicePresidentUserActionType.CHANGE_USER, fieldSalesLeader);
export const saveFieldVicePresidentUser = (fvp: ValidFieldVicePresident, id?: string) =>
  createAction(FieldVicePresidentUserActionType.SAVE_FVP_USER, {fvp, id});

type FieldVicePresidentUserState = {
  fieldVicePresident: Async<FieldVicePresident>;
  regions: Async<ValidRegionCode[]>;
  original: Option<Async<FieldVicePresident>>;
  availableFsls: Async<AvailableFieldSalesLeaderPayload[]>;
};

type FieldVicePresidentUserAction =
  | ReturnType<typeof loadFvpUser>
  | ReturnType<typeof setUsername>
  | ReturnType<typeof setPhone>
  | ReturnType<typeof setEmail>
  | ReturnType<typeof changeAvailableFSLs>
  | ReturnType<typeof changeFieldVicePresidentUser>
  | ReturnType<typeof clearFieldVicePresident>
  | ReturnType<typeof changeRegions>;

export const DEFAULT_STATE: FieldVicePresidentUserState = {
  fieldVicePresident: new Loaded(
    new UnvalidatedFieldVicePresident(
      new EmptyString(),
      new EmptyString(),
      createValidatableEmptyString(),
      createValidatableEmptyPhone(),
      createValidatableEmptyEmail(),
      []
    )
  ),
  availableFsls: new Empty(),
  original: none,
  regions: new Empty(),
};

const fvpLens = Lens.fromProp<FieldVicePresident>();

export default (
  state: FieldVicePresidentUserState = DEFAULT_STATE,
  action: FieldVicePresidentUserAction
): FieldVicePresidentUserState => {
  switch (action.type) {
    case FieldVicePresidentUserActionType.CHANGE_AVAILABLE_FSLS:
      return {
        ...state,
        availableFsls: action.payload,
      };
    case FieldVicePresidentUserActionType.CLEAR:
      return DEFAULT_STATE;
    case FieldVicePresidentUserActionType.SET_EMAIL: {
      return {
        ...state,
        fieldVicePresident: isLoaded(state.fieldVicePresident)
          ? new Loaded(validateFieldVicePresident(fvpLens('email').set(action.payload)(state.fieldVicePresident.Data)))
          : state.fieldVicePresident,
      };
    }
    case FieldVicePresidentUserActionType.SET_USERNAME: {
      return {
        ...state,
        fieldVicePresident: isLoaded(state.fieldVicePresident)
          ? new Loaded(
              validateFieldVicePresident(fvpLens('username').set(action.payload)(state.fieldVicePresident.Data))
            )
          : state.fieldVicePresident,
      };
    }
    case FieldVicePresidentUserActionType.SET_PHONE: {
      return {
        ...state,
        fieldVicePresident: isLoaded(state.fieldVicePresident)
          ? new Loaded(
              validateFieldVicePresident(fvpLens('phoneNumber').set(action.payload)(state.fieldVicePresident.Data))
            )
          : state.fieldVicePresident,
      };
    }
    case FieldVicePresidentUserActionType.LOAD_FVP_USER:
      return {
        ...state,
        fieldVicePresident: action.payload,
        original: some(action.payload),
      };
    case FieldVicePresidentUserActionType.CHANGE_REGIONS:
      return {
        ...state,
        regions: action.payload,
      };
    case FieldVicePresidentUserActionType.CHANGE_USER:
      return {
        ...state,
        fieldVicePresident: action.payload,
      };
    default:
      return state;
  }
};
